import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, Col, Popover, Row, Space, Spin, Statistic, Tag, Tooltip, Typography, Empty } from 'antd';
import { PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { getDashboardCards, getDynamicCommonCount } from '../../services/dashboardApi';
import { getModuleConfig, PALETTE, STICKY_PALETTE, BAR_PALETTE, NAMED_COLORS } from './cardConfig';
import '../../styles/dynamicCard.css';

const { Title, Text } = Typography;

/*
  Rendering engine for the dashboard cards. The WHAT (which widgets exist, their
  colours, what to hide) lives in cardConfig.js — edit that file, not this one.
*/

// Read a field from a record. [a, b] joins them (e.g. first + last name).
// A dotted key ('reminder.time') walks into nested objects.
function read(item, key) {
  if (Array.isArray(key)) return key.map(k => read(item, k)).filter(has).join(' ');
  if (typeof key === 'string' && key.includes('.')) {
    return key.split('.').reduce((o, k) => (o == null ? o : o[k]), item);
  }
  return item[key];
}

const has = v => v != null && v !== '';

function sx(group, key, fallback = {}) {
  return { ...fallback, ...(group.styles?.[key] || {}) };
}

function sectionTitle(group) {
  return has(group.title) ? <Title level={5} style={sx(group, 'headerTitle')}>{group.title}</Title> : undefined;
}

function sectionExtra(group, extra) {
  return has(group.title) ? extra : undefined;
}

function isNamedColor(color) {
  return ['blue', 'green', 'purple', 'peach', 'red', 'gray'].includes(lc(color));
}

function cardSkin(group, color) {
  const token = color || '';
  const baseStyle = sx(group, 'card');
  if (!has(token)) return { className: '', style: baseStyle };
  if (isNamedColor(token)) return { className: token, style: baseStyle };
  return {
    className: '',
    style: {
      ...baseStyle,
      background: token,
      borderColor: group.styles?.card?.borderColor || token,
    },
  };
}

// Walk a dot-path inside the API response ('data.data' -> json.data.data).
function getPath(obj, path) {
  if (!path) return obj;
  return path.split('.').reduce((o, k) => (o == null ? o : o[k]), obj);
}

// Turn 'finalGrossMargin' / 'project_completed' into 'Final Gross Margin'.
function humanize(key) {
  return String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
}

// Turn an ISO date into something readable. The backend uses the year 0001
// ("0001-01-01T00:00:00Z") to mean "no date" — we treat that as empty.
function formatDate(value) {
  if (!has(value)) return null;
  const d = new Date(value);
  if (isNaN(d) || d.getFullYear() <= 1) return null;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
}

/**
 * Collect every config entry for a slot: prefix, prefix2, prefix3 ...
 * Returns the api field names with the HIGHER number first, so adding
 * `titleKey2` stacks it on top of `titleKey`. Infinite by design.
 */
function slotFields(group, prefix) {
  const numbered = new RegExp(`^${prefix}\\d+$`);
  return Object.keys(group)
    .filter(k => k === prefix || numbered.test(k))
    .map(k => ({ field: group[k], n: k === prefix ? 1 : Number(k.slice(prefix.length)) }))
    .sort((a, b) => b.n - a.n)
    .map(m => m.field);
}

// columns -> antd 24-grid span. 3 cols -> 8, 4 cols -> 6.
function spanFor(columns) {
  return Math.max(1, Math.floor(24 / (columns || 4)));
}

// Parse a 'COLSxROWS' layout string -> { columns, maxCount }.
//   '4x2' -> 4 columns, at most 8 cards ; '3x3' -> 3 columns, 9 cards.
function parseLayout(layout) {
  if (typeof layout !== 'string') return {};
  const m = layout.toLowerCase().replace(/\s/g, '').match(/^(\d+)x(\d+)$/);
  if (!m) return {};
  const columns = Number(m[1]);
  return { columns, maxCount: columns * Number(m[2]) };
}

// Resolve the grid for a group from (highest priority first):
//   explicit `columns` / `maxCount`  ->  `layout` ('4x2')  ->  defaultColumns.
// Returns { columns, span, maxCount } — maxCount undefined means "no limit".
function resolveLayout(group, defaultColumns) {
  const lay = parseLayout(group.layout);
  const columns = group.columns ?? lay.columns ?? defaultColumns;
  const maxCount = group.maxCount ?? lay.maxCount;
  return { columns, span: spanFor(columns), maxCount };
}

// Cap a list to maxCount (undefined/0 -> no cap).
function capTo(arr, maxCount) {
  return maxCount ? arr.slice(0, maxCount) : arr;
}

// Color is opt-in per group:
//   no `color`      -> '' (plain white card)
//   color: 'auto'   -> a different palette color per card
//   color: 'green'  -> that fixed color for every card
function cardColor(group, i) {
  if (!group.color) return '';
  if (group.color === 'auto') return PALETTE[i % PALETTE.length];
  return group.color;
}

// lowercase + trim, for case-insensitive name matching.
const lc = v => String(v ?? '').trim().toLowerCase();

// `name` may be a single string OR an array of candidate names (e.g. the raw
// API key AND its humanized label). Returns true if any candidate matches `key`.
function nameEq(key, name) {
  const names = Array.isArray(name) ? name : [name];
  return names.some(n => has(n) && lc(n) === lc(key));
}

// Per-card color override BY NAME (see `colorMap` in the config docs).
// `name` can be one name or several candidates ([rawKey, humanizedLabel]).
// Returns the color token (e.g. 'green' or '#0ea5e9') or null if no match.
function mappedColor(group, name) {
  if (!group.colorMap) return null;
  const hit = Object.keys(group.colorMap).find(k => nameEq(k, name));
  return hit ? group.colorMap[hit] : null;
}

// Per-card LABEL override BY NAME (see `labelMap` in the config docs).
// Matches by raw key OR humanized label; returns `fallback` if no override.
function mappedLabel(group, name, fallback) {
  if (!group.labelMap) return fallback;
  const hit = Object.keys(group.labelMap).find(k => nameEq(k, name));
  return hit ? group.labelMap[hit] : fallback;
}

// Per-card SUB-VALUE shown under the count BY NAME (see `valueMap` in the config).
// Matches by raw key OR humanized label; returns null if no override.
function mappedValue(group, name) {
  if (!group.valueMap) return null;
  const hit = Object.keys(group.valueMap).find(k => nameEq(k, name));
  return hit ? group.valueMap[hit] : null;
}

// Optional allow-list. When present, only these cards render. Names can match
// raw API keys or display labels, same as hide/labelMap.
function isShown(group, name) {
  if (!Array.isArray(group.show) || group.show.length === 0) return true;
  return group.show.some(s => nameEq(s, name));
}

// Should this card be dropped?  hide: [ 'Rejected', 0 ]
//   string -> matches the card NAME (case-insensitive; raw key OR label)
//   number -> matches the card INDEX (0-based, original position)
function isHidden(group, name, index) {
  if (!isShown(group, name)) return true;
  if (!Array.isArray(group.hide)) return false;
  return group.hide.some(h =>
    typeof h === 'number' ? h === index : nameEq(h, name));
}

// The "name" of a list-variant record = its first non-empty titleKey value.
// Used so colorMap / hide can target list cards by their title.
function listItemName(group, item) {
  for (const f of slotFields(group, 'titleKey')) {
    const v = read(item, f);
    if (has(v)) return v;
  }
  return null;
}

// An object of numbers ({ inProgress: 4, hired: 9 }) -> card items.
// Each item keeps its raw `key` so colorMap/hide/labelMap can target a card by
// either the raw API name ('pendingSubmissionCount') or its humanized `label`.
function countsToItems(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
  return Object.entries(obj)
    .filter(([, v]) => typeof v === 'number' || (typeof v === 'string' && v !== '' && !isNaN(v)))
    .map(([k, v]) => ({ key: k, label: humanize(k), value: Number(v) }));
}

function dynamicCommonCountToFlatData(response, group) {
  const data = response?.data ?? response;
  const fields = Array.isArray(data?.fields) ? data.fields : null;
  const preferredKey = group.dynamicCommonCount?.keys?.[0];
  const selectedFields = fields
    ? [
        ...(fields.find(item => item.key === preferredKey) ? [fields.find(item => item.key === preferredKey)] : []),
        ...fields.filter(item => item.key !== preferredKey),
      ]
    : [data];

  if (!selectedFields.some(field => Array.isArray(field?.counts))) return data;

  return selectedFields.reduce((acc, field) => {
    if (!field || !Array.isArray(field.counts)) return acc;
    acc.total = Math.max(Number(acc.total) || 0, Number(field.total) || 0);
    field.counts.forEach(({ key, count }) => {
      acc[key] = Number(count) || 0;
    });
    return acc;
  }, { total: 0 });
}

// A counts object -> the shape ClientSubmissionCard renders:
//   { total, rows: [{ label, value, barColor, accentColor, showInfo }] }
// `totalKey` (if present in the response) is pulled out as the grand total and
// kept out of the rows; otherwise the total is the sum of the row values.
function countsToSubmission(obj, group) {
  const items = countsToItems(obj);
  const totalKey = group.totalKey;
  const totalRaw = totalKey ? read(obj || {}, totalKey) : undefined;
  const hasTotal = totalKey && has(totalRaw) && !isNaN(totalRaw);

  const rows = items
    .map((it, i) => ({ ...it, _i: i }))                              // keep original index
    // drop the total row (matched by raw key OR its humanized label)
    .filter(it => !(hasTotal && nameEq(totalKey, [it.key, it.label])))
    .filter(it => !isHidden(group, [it.key, it.label], it._i))       // drop hidden rows
    .map((it, visible) => {
      // colorMap wins (name -> palette name or raw hex); else cycle the palette.
      // Matched by raw key OR humanized label.
      const mapped = mappedColor(group, [it.key, it.label]);
      const barColor = mapped
        ? (NAMED_COLORS[lc(mapped)] || mapped)
        : BAR_PALETTE[it._i % BAR_PALETTE.length];
      return {
        label: mappedLabel(group, [it.key, it.label], it.label),     // labelMap rename
        value: it.value,
        barColor,
        // The first VISIBLE row carries the orange accent + info icon.
        accentColor: visible === 0 ? '#f97316' : null,
        showInfo: visible === 0,
      };
    });

  // Total reflects ALL rows; the display can still be capped via maxCount.
  const total = hasTotal
    ? Number(totalRaw)
    : rows.reduce((sum, r) => sum + r.value, 0);

  return { total, rows: capTo(rows, resolveLayout(group, 1).maxCount) };
}

function CardItem({ item, group, color }) {
  const lines = key => slotFields(group, key).map(f => read(item, f)).filter(has);

  const titles = lines('titleKey');
  const counts = lines('countKey');
  const names  = lines('nameKey');
  const values = lines('valueKey');

  const skin = cardSkin(group, color);

  return (
    <Card className={`dyn-card-item ${skin.className}`.trim()} style={skin.style} bordered>
      {titles.map((v, i) => <Text key={`t${i}`} className="dyn-card-title" style={sx(group, 'cardTitle')}>{v}</Text>)}
      {counts.map((v, i) => <Statistic key={`c${i}`} value={v} className="dyn-card-stat" valueStyle={sx(group, 'cardValue')} />)}
      {names.map((v, i) => <Text key={`n${i}`} className="dyn-card-name" style={sx(group, 'cardName')}>{v}</Text>)}
      {values.length > 0 && (
        <div className="dyn-card-footer" style={sx(group, 'cardFooter')}>
          {values.map((v, i) => <Text key={`v${i}`} className="dyn-card-sub" style={sx(group, 'cardSub')}>{v}</Text>)}
        </div>
      )}
    </Card>
  );
}

// One section of stat cards built from an array of records (titleKey/countKey…).
function StatGroup({ group }) {
  const { span, maxCount } = resolveLayout(group, 4);
  // Tag each record with its name + original index, drop hidden, then cap.
  const shown = capTo(
    (Array.isArray(group.list) ? group.list : [])
      .map((item, i) => ({ item, name: listItemName(group, item), _i: i }))
      .filter(r => !isHidden(group, r.name, r._i)),
    maxCount,
  );

  return (
    <Card
      className="dyn-card-section"
      style={sx(group, 'section')}
      title={sectionTitle(group)}
      extra={sectionExtra(group, <Text type="secondary" style={sx(group, 'headerExtra')}>{shown.length} cards</Text>)}
    >
      <Row gutter={[16, 16]}>
        {shown.map(r => {
          const color = mappedColor(group, r.name) || cardColor(group, r._i);
          return (
            <Col key={r._i} xs={24} sm={12} md={span >= 12 ? 12 : span * 2} lg={span} xl={span}>
              <CardItem item={r.item} group={group} color={color} />
            </Col>
          );
        })}
      </Row>
    </Card>
  );
}

// One section of stat cards built from a counts object ({ key: number }).
function CountsGroup({ group, items }) {
  const { span, maxCount } = resolveLayout(group, 4);
  // Keep the original index (for stable auto-colors), drop hidden, then cap.
  const shown = capTo(
    items
      .map((it, i) => ({ ...it, _i: i }))
      .filter(it => !isHidden(group, [it.key, it.label], it._i)),
    maxCount,
  );

  return (
    <Card
      className="dyn-card-section"
      style={sx(group, 'section')}
      title={sectionTitle(group)}
      extra={sectionExtra(group, <Text type="secondary" style={sx(group, 'headerExtra')}>{shown.length} cards</Text>)}
    >
      <Row gutter={[16, 16]}>
        {shown.map(it => {
          const color = mappedColor(group, [it.key, it.label]) || cardColor(group, it._i);
          const skin = cardSkin(group, color);
          return (
            <Col key={it._i} xs={24} sm={12} md={span >= 12 ? 12 : span * 2} lg={span} xl={span}>
              <Card className={`dyn-card-item ${skin.className}`.trim()} style={skin.style} bordered>
                <Text className="dyn-card-title" style={sx(group, 'cardTitle')}>
                  {mappedLabel(group, [it.key, it.label], it.label)}
                </Text>
                <Statistic value={it.value} groupSeparator={group.groupSeparator ?? ','} className="dyn-card-stat" valueStyle={sx(group, 'cardValue')} />
                {has(mappedValue(group, [it.key, it.label])) && (
                  <Text className="dyn-card-sub" style={sx(group, 'cardSub')}>
                    {mappedValue(group, [it.key, it.label])}
                  </Text>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>
    </Card>
  );
}

/**
 * Submission variant — the "Total Client Submission" look: a header total and
 * one horizontal progress bar per number in the counts object. Same markup as
 * the stand-alone ClientSubmissionCard, but driven by the common API here so
 * the whole dashboard stays config-driven (no separate component to call).
 */
function SubmissionGroup({ group, data }) {
  const { total, rows } = countsToSubmission(data, group);
  const tooltipText = group.tooltipText;

  return (
    <Card className="submission-card" style={sx(group, 'card')}>

      {has(group.title) && (
        <Row justify="space-between" align="middle" className="submission-header" style={sx(group, 'submissionHeader')}>
          <Col>
            <Text className="submission-title" style={sx(group, 'submissionTitle')}>
              {group.title} {tooltipText && (
                <Tooltip title={tooltipText}>
                  <InfoCircleOutlined className="submission-info-icon" style={sx(group, 'infoIcon')} />
                </Tooltip>
              )}
            </Text>
          </Col>
          <Col>
            <Text className="submission-total" style={sx(group, 'submissionTotal')}>{Number(total || 0).toLocaleString()}</Text>
          </Col>
        </Row>
      )}

      <div className="submission-rows" style={sx(group, 'submissionRows')}>
        {rows.map((r, i) => (
          <div key={`${r.label}-${i}`}>

            <Row justify="space-between" align="middle" className="submission-row-header" style={sx(group, 'submissionRowHeader')}>
              <Col>
                <Row align="middle" gutter={6}>
                  <Col>
                    <Text className="submission-row-label" style={sx(group, 'submissionRowLabel')}>{r.label}</Text>
                  </Col>
                  {/* {r.showInfo && (
                    <Col>
                      <Tooltip title={group.infoText}>
                        <InfoCircleOutlined className="submission-info-icon" />
                      </Tooltip>
                    </Col>
                  )} */}
                </Row>
              </Col>
              <Col>
                <Text className="submission-row-value" style={sx(group, 'submissionRowValue')}>
                  {r.value}
                </Text>
              </Col>
            </Row>

            <div className="submission-bar-track" style={sx(group, 'submissionBarTrack')}>
              {/* {r.accentColor && (
                <div className="submission-bar-accent" style={{ background: r.accentColor }} />
              )} */}
             <div
  className="submission-bar-fill"
  style={{
    width: `${(r.value / (total || 1)) * 100}%`,
    background: r.barColor,
  }}
/>
            </div>

          </div>
        ))}
      </div>

    </Card>
  );
}

/**
 * Map one API record to a sticky note, using the *Key fields from the group
 * config. Every field is optional — leave a key out and that part won't show.
 *   pillKey / headingKey / bodyKey / footerLeftKey   -> plain text
 *   footerRightKey / dateKey                          -> formatted as a date
 * Colour priority:
 *   colorMap[<colorBy field value, e.g. status>]  ->  fixed `color`  ->  auto cycle
 */
function toStickyNote(item, group, i) {
  const reminder = group.dateKey ? formatDate(read(item, group.dateKey)) : null;
  const colorName = group.colorBy ? read(item, group.colorBy) : null;
  const type =
    mappedColor(group, colorName) ||
    (group.color && group.color !== 'auto'
      ? group.color
      : STICKY_PALETTE[i % STICKY_PALETTE.length]);
  return {
    type,
    pill: group.pillKey ? read(item, group.pillKey) : null,
    heading: group.headingKey ? read(item, group.headingKey) : null,
    body: group.bodyKey ? read(item, group.bodyKey) : null,
    footerLeft: group.footerLeftKey ? read(item, group.footerLeftKey) : null,
    footerRight: group.footerRightKey ? formatDate(read(item, group.footerRightKey)) : null,
    dot: reminder ? '#f97316' : null,   // a reminder date gets the orange dot
    date: reminder,
  };
}

/**
 * Sticky-note body text. The card is a fixed height (see dynamicCard.css) and the
 * body is clamped to `bodyLines` lines with a CSS ellipsis. We measure the node
 * and ONLY when the text is actually clipped wrap it in a hover Popover that
 * shows the full content. Re-measures on resize (column width changes clamping).
 */
function StickyNoteBody({ text, lines, style }) {
  const ref = useRef(null);
  const [clipped, setClipped] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    // Measure on the next frame so fonts / flex layout have settled first.
    const check = () => {
      raf = requestAnimationFrame(() => {
        const next = el.scrollHeight - el.clientHeight > 1;
        // Bail if unchanged — without this, a value that keeps recomputing to
        // the same boolean would still re-run the effect below on every tick.
        setClipped((prev) => (prev === next ? prev : next));
      });
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [text, lines]);

  const className = `sticky-note-body${lines ? ` clamp-${lines}` : ''}`;
  const body = <div ref={ref} className={className} style={style}>{text}</div>;

  // Always wrap in the SAME Popover, whether clipped or not — swapping the
  // wrapper in/out based on `clipped` changes the observed div's effective
  // layout width, which can flip the clipped measurement back and forth and
  // re-trigger the ResizeObserver forever. `open={false}` keeps it inert
  // (no hover popover) when the text isn't actually clipped.
  return (
    <Popover
      trigger="hover"
      placement="topLeft"
      overlayClassName="sticky-note-popover"
      content={clipped ? <div className="sticky-note-popover-content">{text}</div> : null}
      open={clipped ? undefined : false}
    >
      {body}
    </Popover>
  );
}

/**
 * Sticky Notes section — same look as the stand-alone StickyNotesCard, but
 * rendered here so the whole dashboard is driven by DynamicCards alone.
 */
function StickyGroup({ group }) {
  const navigate = useNavigate();
  const location = useLocation();
  const openAddNote = () => navigate('/popup/sticky-notes', {
    state: {
      background: location,
      selectedIds: [],
      selectedRecords: [],
      // 'general' is the notesFor category the notes collection accepts for a
      // dashboard-level note with no related job/candidate/submission record —
      // NOT group.moduleName (that's the dashboard-card key, e.g. "stickyNotes").
      moduleName: 'general',
      popupContext: {},
    },
  });
  // Sticky notes default to 2 columns; `layout`/`columns`/`maxCount` override.
  const { span, maxCount } = resolveLayout(group, 2);
  // A note's name = its colorBy field (e.g. status), else its heading.
  // Keep the original index so the auto colour-cycle stays stable after hiding.
  const noteName = item =>
    group.colorBy ? read(item, group.colorBy)
    : group.headingKey ? read(item, group.headingKey)
    : null;
  const list = capTo(
    (Array.isArray(group.list) ? group.list : [])
      .map((item, i) => ({ item, _i: i }))
      .filter(({ item, _i }) => !isHidden(group, noteName(item), _i)),
    maxCount,
  );
  return (
    <Card
      className="sticky-card"
      style={sx(group, 'section')}
      title={sectionTitle(group)}
      extra={sectionExtra(group,
        <div className="sticky-card-actions">
          <Tooltip title="Add note">
            <Button type="text" icon={<PlusOutlined />} size="small" onClick={openAddNote} />
          </Tooltip>
          {has(group.viewAllUrl) && (
            <Button
              type="link"
              size="small"
              onClick={() => navigate(group.viewAllUrl, {
                // Hand the full-page route everything it needs to re-fetch the
                // SAME list (module + its API request) — minus the dashboard cap.
                state: {
                  moduleName: group.moduleName,
                  title: group.title,
                  request: group.request,
                },
              })}
            >
              View all
            </Button>
          )}
        </div>
      )}
    >
      <Row gutter={[12, 12]}>
        {list.map(({ item, _i }) => {
          const n = toStickyNote(item, group, _i);
          const skin = cardSkin(group, n.type);
          return (
            <Col key={_i} xs={24} sm={24} md={span >= 12 ? 12 : span * 2} lg={span} xl={span}>
              <div className={`sticky-note ${skin.className}`.trim()} style={skin.style}>

                <Space size={8} align="center" className="sticky-note-pill-row">
                  {has(n.pill) && (
                    <Tag style={sx(group, 'stickyPill')}>
                      {n.dot && <span className="sticky-note-dot" style={{ background: n.dot }} />}
                      {n.pill}
                    </Tag>
                  )}
                  {n.date && <Text className="sticky-note-date" style={sx(group, 'stickyDate')}>{n.date}</Text>}
                </Space>

                {has(n.heading) && <Text strong className="sticky-note-heading" style={sx(group, 'stickyHeading')}>{n.heading}</Text>}
                {has(n.body) && <StickyNoteBody text={n.body} lines={group.bodyLines} style={sx(group, 'stickyBody')} />}

                <Row justify="space-between" align="middle" className="sticky-note-footer" style={sx(group, 'stickyFooter')}>
                  <Col><Text style={sx(group, 'stickyFooterText')}>{n.footerLeft}</Text></Col>
                  <Col><Text style={sx(group, 'stickyFooterText')}>{n.footerRight}</Text></Col>
                </Row>

              </div>
            </Col>
          );
        })}
      </Row>
    </Card>
  );
}

/**
 * Optional outer "frame" around a group — an opt-in titled card with a View-all
 * link and (optionally) a footer strip. Turn it on per module in the config:
 *   frame: true            -> wrap the group in this outer card
 *   frameTitle: 'Onboarding'-> the heading text
 *   frameClassName: 'x'    -> class on the outer card (styled in CSS; also drives
 *                             the footer class `<frameClassName>-footer`)
 *   viewAll: true | 'text' -> show a View-all link in the header
 *   footer: true           -> render the footer strip
 * The inner group renders WITHOUT its own title, so there's no double header.
 */
function FrameCard({ group, children }) {
  const cls = group.frameClassName || 'dyn-frame';
  const extra = group.viewAll
    ? <a className="dyn-frame-view-all">{typeof group.viewAll === 'string' ? group.viewAll : 'View all'}</a>
    : undefined;
  return (
    <Card className={cls} title={group.frameTitle} extra={extra}>
      {children}
      {group.footer && <div className={`${cls}-footer`} />}
    </Card>
  );
}

// Render a single resolved group (config + already-extracted data).
function GroupView({ group, data }) {
  // A framed group never renders its own inner title — the frame owns the header.
  const inner = group.frame ? { ...group, title: undefined } : group;
  const content =
    inner.variant === 'sticky'
      ? <StickyGroup group={{ ...inner, list: Array.isArray(data) ? data : [] }} />
      : inner.variant === 'counts'
        ? <CountsGroup group={inner} items={countsToItems(data)} />
        : inner.variant === 'submission'
          ? <SubmissionGroup group={inner} data={data} />
          : <StatGroup group={{ ...inner, list: Array.isArray(data) ? data : [] }} />;

  return group.frame ? <FrameCard group={group}>{content}</FrameCard> : content;
}

/**
 * ModuleCards — API-driven mode. Give it a moduleName, it looks the config up
 * in MODULE_CONFIG, calls the ONE common /dashboard/cards endpoint, then renders
 * the response with the same sticky/counts/list logic used by the static mode.
 */
function ModuleCards({ moduleName, title, maxCount, layout, columns, request, viewAll = true, staticData }) {
  const base = getModuleConfig()[moduleName];
  // Props passed from the page override the matching keys in cardConfig.js.
  // Memoised so the fetch effect below doesn't re-run on every render.
  // `request` is stringified for the dep array so a fresh object (e.g. one
  // coming from router navigation state) doesn't re-trigger the effect.
  const requestKey = request ? JSON.stringify(request) : '';
  const config = useMemo(() => {
    if (!base) return base;
    const overrides = { maxCount, layout, columns };
    // moduleName is kept on the config so children (e.g. the sticky "View all"
    // link) can forward it to the full-page route.
    const merged = { ...base, title, moduleName };
    for (const k in overrides) {
      if (has(overrides[k])) merged[k] = overrides[k];
    }
    // Merge a request override (e.g. a larger limit on the "View all" page).
    if (request) {
      merged.request = {
        ...base.request,
        ...request,
        params: { ...base.request?.params, ...request.params },
        body: { ...base.request?.body, ...request.body },
      };
    }
    // Let a page suppress the "View all" link (so the full page doesn't show
    // a link back to itself).
    if (!viewAll) merged.viewAllUrl = undefined;
    return merged;
  }, [base, title, maxCount, layout, columns, requestKey, viewAll]);
  // One state bag tagged with the module it belongs to, so we never call
  // setState synchronously inside the effect (only in the async callbacks).
  const [result, setResult] = useState({ moduleName: null, resp: null, error: null });

  // Bump on config.refreshEvent (e.g. a popup dispatches it after a successful
  // save) to force the fetch effect below to re-run — closing a popup returns
  // to this same mounted component, so nothing else would trigger a refetch.
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    if (!config?.refreshEvent) return undefined;
    const bump = () => setRefreshTick((t) => t + 1);
    window.addEventListener(config.refreshEvent, bump);
    return () => window.removeEventListener(config.refreshEvent, bump);
  }, [config?.refreshEvent]);

  useEffect(() => {
    if (!config) return;

    // If a parent already has count data, convert and use it.
    if (staticData !== undefined) {
      const resp = dynamicCommonCountToFlatData(staticData, config);
      setResult({ moduleName, resp, error: null });
      return;
    }

    if (config.dynamicCommonCount) {
      let alive = true;
      getDynamicCommonCount(config.dynamicCommonCount)
        .then(json => {
          if (alive) setResult({ moduleName, resp: dynamicCommonCountToFlatData(json, config), error: null });
        })
        .catch(err => { if (alive) setResult({ moduleName, resp: null, error: err }); });
      return () => { alive = false; };
    }

    // For non-count modules (e.g. stickyNotes), fetch individually.
    if (!config.request) return;
    let alive = true;
    getDashboardCards(moduleName, config.request)
      .then(json => { if (alive) setResult({ moduleName, resp: json, error: null }); })
      .catch(err => { if (alive) setResult({ moduleName, resp: null, error: err }); });
    return () => { alive = false; };
  }, [moduleName, config, staticData, refreshTick]);

  // Still loading until the result we hold is for THIS module.
  const loading = result.moduleName !== moduleName;
  const error = loading ? null : result.error;
  const resp = loading ? null : result.resp;

  if (!config) {
    return <Empty description={`Unknown dashboard module: ${moduleName}`} />;
  }
  if (loading) {
    return (
      <Card className="dyn-card-section" style={sx(config, 'section')} title={sectionTitle(config)}>
        <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
      </Card>
    );
  }
  if (error) {
    return (
      <Card className="dyn-card-section" style={sx(config, 'section')} title={sectionTitle(config)}>
        <Empty description={`Failed to load: ${error.message}`} />
      </Card>
    );
  }

  return <GroupView group={config} data={getPath(resp, config.listPath)} />;
}

/**
 * DynamicCards — give it a moduleName and it renders that dashboard widget LIVE
 * from the common API. Any of these props override the config in cardConfig.js:
 *   title    -> section heading
 *   layout   -> 'COLSxROWS' grid, e.g. '4x2' (4 wide, max 8 cards), '3x3', '2x4'
 *   columns  -> just the column count (no card cap)
 *   maxCount -> hard cap on how many cards to show
 *
 *   <DynamicCards moduleName="jobsCount" layout="4x2" />
 *   <DynamicCards moduleName="stickyNotes" title="My Notes" maxCount={6} />
 */
export default function DynamicCards({ moduleName, title, layout, columns, maxCount, request, viewAll, staticData }) {
  if (!moduleName) {
    return <Empty description="DynamicCards: a moduleName prop is required" />;
  }

  return (
    <div className="dyn-cards">
      <ModuleCards
        moduleName={moduleName}
        title={title}
        layout={layout}
        columns={columns}
        maxCount={maxCount}
        request={request}
        viewAll={viewAll}
        staticData={staticData}
      />
    </div>
  );
}
